"use client";

import Image from "next/image";
import Link from "next/link";
import { ScrollReveal } from "../shared";
import { ShieldCheckIcon, ActivityIcon, HandHeartIcon } from "../shared/icons";
import { walletArt } from "../shared/walletArt";

type Proof = {
  icon: typeof ShieldCheckIcon;
  label: string;
  detail: string;
  accent: string;
};

const PROOF: Proof[] = [
  {
    icon: ShieldCheckIcon,
    label: "Evidence-backed",
    detail: "Every risk verdict links to the on-chain activity behind it.",
    accent: "#00D4FF",
  },
  {
    icon: ActivityIcon,
    label: "Always current",
    detail: "Wallet movement is monitored continuously, not snapshotted.",
    accent: "#A855F7",
  },
  {
    icon: HandHeartIcon,
    label: "Built for trust",
    detail: "Verification, reporting, and giving transparency in one place.",
    accent: "#22C55E",
  },
];

/**
 * Closing conversion band, sitting between the stats strip and the footer.
 *
 * Added in the 2026-08-04 premium pass. The landing page previously ended
 * on a stats row and dropped straight into the footer, so a visitor who
 * scrolled the whole page had no call to action in front of them at the
 * moment they'd finished evaluating - they had to scroll back to the hero.
 * This closes that loop: one restated value proposition, three proof
 * points, and the same two CTAs as the hero (identical `.pr-btn`
 * treatment, so it reads as a deliberate bookend rather than a new
 * component).
 *
 * All links point at existing routes - no new pages are implied.
 */
export default function ClosingCTA() {
  return (
    <section aria-labelledby="closing-cta-heading" className="page-section relative w-full py-10 sm:py-14">
      <ScrollReveal className="mx-auto max-w-6xl">
        <div
          className="section-panel relative overflow-hidden rounded-[32px] border px-6 py-10 sm:px-12 sm:py-14"
          style={{
            borderColor: "rgba(0,245,255,0.20)",
            background: "linear-gradient(180deg, rgba(24,36,68,0.92), rgba(14,22,42,0.97))",
            boxShadow:
              "0 0 0 1px rgba(0,245,255,0.08) inset, 0 36px 90px -46px rgba(0,0,0,0.9), 0 18px 50px -32px rgba(20,124,255,0.45)",
          }}
        >
          {/* Ambient corner glows, matching the hero panel so the page opens
              and closes on the same visual note. */}
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div
              className="absolute -left-20 -top-24 h-72 w-72 rounded-full opacity-40 blur-3xl"
              style={{ background: "radial-gradient(circle, rgba(0,212,255,0.22), transparent 70%)" }}
            />
            <div
              className="absolute -bottom-24 -right-16 h-72 w-72 rounded-full opacity-35 blur-3xl"
              style={{ background: "radial-gradient(circle, rgba(124,58,237,0.24), transparent 70%)" }}
            />
          </div>

          <div className="relative z-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="min-w-0">
              <span className="eyebrow-label">Start in seconds</span>
              <h2 id="closing-cta-heading" className="pr-section-title headline-balance mt-4">
                Know who you&apos;re dealing with
                <span className="mt-1 block bg-[linear-gradient(90deg,#eaf0ff_0%,#9ae6ff_34%,#4fb2ff_66%,#d8b4fe_100%)] bg-clip-text text-transparent">
                  before the transaction, not after.
                </span>
              </h2>
              <p className="pr-section-sub">
                Paste any wallet address, transaction hash, ENS name, or domain. Reqrium returns the risk
                picture, the entity behind it, and the activity that supports the verdict — no account
                required to look.
              </p>

              {/* Primary CTA (client, 2026-08-05): surfaced with
                  public/wallet/walletAnalysis.png, matching the treatment on
                  the hero's "Start Wallet Analysis" button. Same .pr-btn
                  geometry as the ghost button beside it, so the pair stays
                  visually balanced; the scrim keeps the label readable over
                  the artwork's bright areas. */}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <Link href="/wallet-analysis" className="pr-btn pr-btn-image pr-btn-fluid">
                  <Image
                    src={walletArt("walletAnalysis.png")}
                    alt=""
                    fill
                    sizes="(max-width: 640px) 16rem, 16rem"
                    className="pr-btn-image-art"
                    aria-hidden="true"
                  />
                  <span className="pr-btn-image-scrim" aria-hidden="true" />
                  <span>Analyze a wallet</span>
                </Link>
                <Link href="/login" className="pr-btn pr-btn-ghost pr-btn-fluid">
                  <span>Claim your profile</span>
                </Link>
              </div>
            </div>

            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {PROOF.map((item) => {
                const Icon = item.icon;
                return (
                  <li
                    key={item.label}
                    className="pr-card flex items-start gap-4 p-4 sm:p-5"
                  >
                    <span
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border"
                      style={{
                        borderColor: `${item.accent}33`,
                        backgroundColor: `${item.accent}1a`,
                        color: item.accent,
                      }}
                      aria-hidden="true"
                    >
                      <Icon size={19} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-100">{item.label}</span>
                      <span className="mt-1 block text-[13px] leading-relaxed text-slate-400">
                        {item.detail}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </ScrollReveal>
    </section>
  );
}
